import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Pause, Square, RotateCcw, Coffee, CheckCircle2 } from 'lucide-react';
import { getTasks, saveTasks } from '../utils/storage';

const FocusMode = ({ taskId, onClose }) => {
  const [timeLeft, setTimeLeft] = useState(25 * 60); // 25 minutes in seconds
  const [isRunning, setIsRunning] = useState(false);
  const [isBreak, setIsBreak] = useState(false);
  const [completedSessions, setCompletedSessions] = useState(0);
  const intervalRef = useRef(null);
  const navigate = useNavigate();

  const tasks = getTasks();
  const task = tasks.find(t => t.id === taskId);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(time => time - 1);
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isRunning, timeLeft]);

  useEffect(() => {
    if (timeLeft === 0) {
      handleTimerComplete();
    }
  }, [timeLeft]);

  const handleTimerComplete = () => {
    setIsRunning(false);

    if (!isBreak) {
      // Work session completed
      setCompletedSessions(prev => prev + 1);

      if (completedSessions + 1 >= 4) {
        // Long break after 4 sessions
        setTimeLeft(15 * 60); // 15 minutes
        setIsBreak(true);
        showNotification('Great work! Take a 15-minute break.');
      } else {
        // Short break
        setTimeLeft(5 * 60); // 5 minutes
        setIsBreak(true);
        showNotification('Well done! Take a 5-minute break.');
      }
    } else {
      // Break completed
      setTimeLeft(25 * 60); // Back to work
      setIsBreak(false);
      showNotification('Break time over! Ready to focus again?');
    }
  };

  const showNotification = (message) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('StudySnap Focus Mode', { body: message });
    }
  };

  const toggleTimer = () => {
    setIsRunning(!isRunning);
  };

  const resetTimer = () => {
    setIsRunning(false);
    setTimeLeft(isBreak ? (completedSessions >= 4 ? 15 * 60 : 5 * 60) : 25 * 60);
  };

  const stopSession = () => {
    setIsRunning(false);
    onClose();
  };

  const completeTask = () => {
    const allTasks = getTasks();
    const taskIndex = allTasks.findIndex(t => t.id === taskId);
    if (taskIndex >= 0) {
      allTasks[taskIndex].completed = true;
      allTasks[taskIndex].completedAt = new Date().toISOString();
      saveTasks(allTasks);
    }
    onClose();
    navigate('/');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = ((isBreak ? (completedSessions >= 4 ? 15 * 60 : 5 * 60) : 25 * 60) - timeLeft) /
                   (isBreak ? (completedSessions >= 4 ? 15 * 60 : 5 * 60) : 25 * 60) * 100;

  return (
    <div className="focus-mode-overlay">
      <div className="focus-mode-container">
        <div className="focus-mode-header">
          <h2>Focus Mode</h2>
          <button onClick={stopSession} className="close-btn">
            <Square size={20} />
          </button>
        </div>

        {task && (
          <div className="focus-task-info">
            <h3>{task.title}</h3>
            <div className="focus-meta">
              <span className="session-count">
                Session {completedSessions + 1} of 4
              </span>
              <span className={`session-type ${isBreak ? 'break' : 'work'}`}>
                {isBreak ? <Coffee size={16} /> : <CheckCircle2 size={16} />}
                {isBreak ? 'Break Time' : 'Focus Time'}
              </span>
            </div>
          </div>
        )}

        <div className="timer-display">
          <div className="timer-circle">
            <svg width="200" height="200" viewBox="0 0 200 200">
              <circle
                cx="100"
                cy="100"
                r="90"
                stroke="var(--border)"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="100"
                cy="100"
                r="90"
                stroke={isBreak ? "var(--warning)" : "var(--primary)"}
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 90}`}
                strokeDashoffset={`${2 * Math.PI * 90 * (1 - progress / 100)}`}
                transform="rotate(-90 100 100)"
                strokeLinecap="round"
              />
            </svg>
            <div className="timer-text">
              <div className="time">{formatTime(timeLeft)}</div>
              <div className="timer-label">
                {isBreak ? 'Break' : 'Focus'}
              </div>
            </div>
          </div>
        </div>

        <div className="timer-controls">
          <button onClick={toggleTimer} className="timer-btn primary">
            {isRunning ? <Pause size={24} /> : <Play size={24} />}
            {isRunning ? 'Pause' : 'Start'}
          </button>
          <button onClick={resetTimer} className="timer-btn secondary">
            <RotateCcw size={20} />
            Reset
          </button>
        </div>

        {task && (
          <div className="focus-actions">
            <button onClick={completeTask} className="complete-task-btn">
              <CheckCircle2 size={20} />
              Complete Task
            </button>
          </div>
        )}

        <div className="focus-tips">
          <p>
            {isBreak
              ? "Take a moment to stretch, hydrate, or step away from your screen."
              : "Stay focused on your task. Minimize distractions and work steadily."
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default FocusMode;
